import React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ExternalLink, FileSpreadsheet } from "lucide-react";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-CA", { timeZone: "America/Toronto", dateStyle: "medium", timeStyle: "short" }) : "—");

// Settings card: daily Google-Sheet backup of all orders (5pm Toronto) + "Back up now" + recent runs.
export function OrdersBackupCard() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["orders-backup"], queryFn: async () => (await api.get("/orders-backup")).data });
  const run = useMutation({
    mutationFn: async () => (await api.post("/orders-backup/run")).data,
    onSuccess: (r) => { qc.invalidateQueries({ queryKey: ["orders-backup"] }); toast.success(`Backup saved · ${r.rows} orders → ${r.name}`); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Backup failed"),
  });
  const items = data?.items || [];
  const last = items[0];
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="orders-backup-card">
      <div className="flex items-center gap-2 flex-wrap">
        <FileSpreadsheet size={22} className="text-emerald-700" />
        <h2 className="font-semibold text-lg">Daily orders backup (Google Sheet)</h2>
        {data && (
          <Badge className={data.configured ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-amber-50 text-amber-800 border border-amber-200"} data-testid="orders-backup-status">
            {data.configured ? "Scheduled" : "Not configured"}
          </Badge>
        )}
      </div>
      <p className="text-sm text-slate-600 mt-2">
        {data?.schedule || "Every day at 5:00 pm (Toronto time)"} a new sheet <b>LPC Orders Backup YYYY-MM-DD</b> is saved in the Drive folder{" "}
        {data?.folder_url ? (
          <a href={data.folder_url} target="_blank" rel="noreferrer" className="underline text-sky-700 inline-flex items-center gap-1" data-testid="orders-backup-folder-link">BACKUP LPC <ExternalLink size={12} /></a>
        ) : "BACKUP LPC"}
        . Columns: Order #, Shopify #, Stage, Tab (left-menu tab), customer, vehicle, colour, catalog #, tracking #, timestamps — plus a Summary sheet with orders per tab.
        Uses the connected Google Drive account.
      </p>
      <div className="mt-4 flex items-center gap-3 flex-wrap">
        <Button size="sm" className="bg-emerald-700 hover:bg-emerald-800 text-white" disabled={run.isPending} onClick={() => run.mutate()} data-testid="orders-backup-run-btn">
          {run.isPending ? "Backing up…" : "Back up now"}
        </Button>
        {last && (
          <span className="text-xs text-slate-600" data-testid="orders-backup-last">
            Last run: {fmt(last.started_at)} · {last.status === "ok" ? `${last.rows} orders` : last.status}
            {last.url && <> · <a href={last.url} target="_blank" rel="noreferrer" className="underline text-sky-700">open sheet</a></>}
          </span>
        )}
      </div>
      {isLoading && <div className="text-xs text-slate-500 mt-3">Loading…</div>}
      {items.length > 0 && (
        <div className="mt-4 rounded-md border border-slate-200 overflow-hidden">
          <table className="w-full text-xs" data-testid="orders-backup-table">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
              <tr><th className="px-3 py-2 text-left">When</th><th className="px-3 py-2 text-left">File</th><th className="px-3 py-2 text-left">Orders</th><th className="px-3 py-2 text-left">Trigger</th><th className="px-3 py-2 text-left">Status</th></tr>
            </thead>
            <tbody>
              {items.slice(0, 10).map((b) => (
                <tr key={b.id} className="border-t" data-testid={`orders-backup-row-${b.id}`}>
                  <td className="px-3 py-1.5 whitespace-nowrap">{fmt(b.started_at)}</td>
                  <td className="px-3 py-1.5">{b.url ? <a href={b.url} target="_blank" rel="noreferrer" className="underline text-sky-700">{b.name}</a> : b.name}</td>
                  <td className="px-3 py-1.5">{b.rows ?? "—"}</td>
                  <td className="px-3 py-1.5 font-mono">{(b.trigger || "").replace("manual:", "manual · ")}</td>
                  <td className="px-3 py-1.5">
                    {b.status === "ok" ? <span className="text-emerald-700 font-semibold">✓ saved</span> : b.status === "running" ? <span className="text-slate-500">running…</span> : b.status === "skipped" ? <span className="text-slate-500" title={b.error}>skipped (preview)</span> : <span className="text-red-700" title={b.error}>✗ {b.error ? b.error.slice(0, 80) : "failed"}</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
